import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

import { Button, ButtonColor } from '@shared/components/button/button';

import { Modal, ModalFooter } from './modal';

/** Severity of the question, which drives the icon and the confirm button. */
export type ConfirmTone = 'info' | 'warning' | 'danger';

export interface ConfirmModalData {
  /** Translation key — resolved here, not at the call site. */
  title: string;
  /** Translation key for the question itself. */
  message: string;
  /**
   * Interpolation values for `title` and `message`, e.g. `{ name }` for a
   * `'{{name}}'` placeholder. Same contract as the snackbar's `params`.
   */
  params?: Record<string, unknown>;
  /** Translation key overriding the generic "Confirm". */
  confirmText?: string;
  /** Translation key overriding the generic "Cancel". */
  cancelText?: string;
  tone?: ConfirmTone;
}

const TONE_ICONS: Record<ConfirmTone, string> = {
  info: 'help_outline',
  warning: 'warning',
  danger: 'error_outline',
};

const TONE_COLORS: Record<ConfirmTone, ButtonColor> = {
  info: 'primary',
  warning: 'primary',
  danger: 'danger',
};

/**
 * The generic yes/no question, replacing the old app's `popUpConfirm` and its
 * per-feature copies. Features never open it directly — `ModalService.confirm()`
 * does, and hands back a plain `Promise<boolean>`.
 *
 * Copy arrives as translation keys rather than final text, unlike the
 * `sci-modal` inputs: this is opened from TypeScript, where there is no
 * `translate` pipe to apply. Same contract as `SnackbarService`.
 *
 * Nothing carries `cdkFocusInitial`, so focus lands on the header's ✕ — the
 * first tabbable element. That is deliberate for a component whose confirm
 * button may well delete something: Enter on open must not destroy anything.
 */
@Component({
  selector: 'app-confirm-modal',
  imports: [Modal, ModalFooter, Button, MatIconModule, TranslatePipe],
  templateUrl: './confirm-modal.html',
  styleUrl: './confirm-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.confirm--warning]': 'tone === "warning"',
    '[class.confirm--danger]': 'tone === "danger"',
  },
})
export class ConfirmModal {
  protected readonly ref = inject<MatDialogRef<ConfirmModal, boolean>>(MatDialogRef);
  protected readonly data = inject<ConfirmModalData>(MAT_DIALOG_DATA);

  protected readonly tone: ConfirmTone = this.data.tone ?? 'info';
  protected readonly icon = TONE_ICONS[this.tone];
  protected readonly confirmColor = TONE_COLORS[this.tone];
  protected readonly confirmText = this.data.confirmText ?? 'components.confirmModal.confirm';
  protected readonly cancelText = this.data.cancelText ?? 'components.confirmModal.cancel';
}
